/* eslint-disable camelcase */
import React from "react"
import { useSelector } from "react-redux"

import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_2oo2,
} from "const"
import { SilResultSubsystem } from "server/models"
import { LangType, StoreType, UserSettings } from "client/store"

import * as Const from "client/components/pdfmake/template/const"
import FormatPercentage, { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../../styled"


interface Iec615081998Props {
    subsystemList: readonly SilResultSubsystem[]
    showArchitectureConstraints: boolean
}

export const Iec615081998 = ({
    subsystemList,
    showArchitectureConstraints,
}: Iec615081998Props) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <BlockContent>
            <ResultTable>
                <thead>
                    <tr>
                        <th>{lang.subsystem}</th>
                        <th>{lang.architecture}</th>
                        {
                            showArchitectureConstraints
                            && <th>{lang.architecturalConstraints}</th>
                        }
                        <th>{lang.t1}</th>
                        <th>{lang.mttr}</th>
                        <th>{lang.beta}</th>
                    </tr>
                </thead>

                <tbody>
                    {
                        subsystemList.map((subsystem) => {
                            const t1 = subsystem.iec615081998t1
                            const mttr = subsystem.iec615081998mttr
                            let beta: string | JSX.Element = <FormatPercentage data={subsystem.iec615081998beta} tableView />

                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_2oo2
                            ) {
                                beta = lang.n_a
                            }

                            return (
                                <tr key={subsystem.id}>
                                    <td>{subsystem.name}</td>
                                    <td>{subsystem.architecture}</td>
                                    {
                                        showArchitectureConstraints
                                        && <td>{lang[subsystem.architecturalConstraints]}</td>
                                    }
                                    <td>{t1}</td>
                                    <td>{mttr}</td>
                                    <td>{beta}</td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>{`${lang.n_a} = ${lang.notApplicable}`}</ResultTableAnnotation>
        </BlockContent>
    )
}


export const Iec615081998PdfExport = (
    subsystemList: readonly SilResultSubsystem[],
    showArchitectureConstraints: boolean,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const stack: any[] = []

    subsystemList.forEach((subsystem) => {
        const t1 = subsystem.iec615081998t1
        const mttr = subsystem.iec615081998mttr
        let beta = FormatPercentageString(subsystem.iec615081998beta, userSettings, lang)

        if (
            subsystem.architecture === ARCHITECTURE_1oo1
            || subsystem.architecture === ARCHITECTURE_2oo2
        ) {
            beta = null
        }

        const list: string[] = [
            `${lang.architecture}: ${subsystem.architecture}`,
        ]
        if (showArchitectureConstraints) {
            list.push(`${lang.architecturalConstraints}: ${lang[subsystem.architecturalConstraints]}`)
        }
        list.push(
            `${lang.t1}: ${t1} ${lang.hours}`,
            `${lang.mttr}: ${mttr} ${lang.hours}`,
        )
        if (beta !== null) list.push(`${lang.beta}: ${beta}`)

        stack.push(
            {
                bold: true,
                text: `${lang.subsystem} - ${subsystem.name}`,
                margin: Const.SUBSYSTEM_NAME_MARGIN,
            },
            {
                style: Const.LIST,
                ul: list,
            },
        )
    })

    return stack
}
